import React, { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import Meta from "../../components/Meta";
import PlanningApplicationModal from "../../components/planning/PlanningApplicationModal";
import CouncilDecisionFacts from "../../components/planning/CouncilDecisionFacts";
import ImageModal from "../../components/planning/common/ImageModal";
import { openPlanningDoc } from "../../utils/openPlanningDoc";
import { PlanningContextProvider, usePlanningContext } from "../../components/planning/state/PlanningContext";
import {
  fetchApplicationById,
  fetchApplications as fetchApplicationsApi,
} from "../../lib/services/applicationsApiService";
import { SITE_URL } from "../../lib/siteUrl";
import { parsePlanningDate as parseDate, formatLongPlanningDate as formatLongDate } from "../../lib/planningDates";
import { buildPlanningApplicationPath } from "../../lib/planningApplicationUrl";
import { buildPlanningArchivePath } from "../../lib/planningArchive";
import {
  DEFAULT_COUNCIL_SLUG,
  applicationIdentityKey,
  formatCouncilQualifiedReference,
  getCouncilConfig,
  isLegacyCouncil,
  isLondonCouncil,
} from "../../lib/councils";
import {
  fetchPlanningArticleByReference,
  parseMarkdownToBlogSections,
} from "../../lib/planningArticles";
import {
  getPlanningSeoAuthorityName,
  getPlanningSeoPlaceName,
} from "../../lib/planningSeo";
import { getMapSnapshotUrl } from "../../lib/planning/mapSnapshotUrl";
import { isSourceOnlyPlanningRecord } from "../../lib/planning/londonSharedDelivery";
import { sanitizePublicPlanningRecord } from "../../lib/planning/publicReleasePolicy";

const CACHE_TTL_MS = 5 * 60 * 1000;
const DETAIL_PAGE_S_MAXAGE_SECONDS = 5 * 60;
const DETAIL_PAGE_STALE_WHILE_REVALIDATE_SECONDS = 30 * 60;
const DETAIL_PAGE_NOT_FOUND_S_MAXAGE_SECONDS = 60;
export const DETAIL_PAGE_REVALIDATE_SECONDS = 24 * 60 * 60;
export const DETAIL_PAGE_DECIDED_REVALIDATE_SECONDS = 7 * 24 * 60 * 60;
export const DETAIL_PAGE_ARCHIVED_REVALIDATE_SECONDS = 30 * 24 * 60 * 60;
const DETAIL_PAGE_NOT_FOUND_REVALIDATE_SECONDS = 5 * 60;
export const DETAIL_PAGE_RECOVERY_REVALIDATE_SECONDS = 30;
const DETAIL_PAGE_ARCHIVE_AFTER_MS = 90 * 24 * 60 * 60 * 1000;
// The API can briefly queue a cold detail lookup behind the public feed/read
// model. Keep the request bounded, but retry one transient upstream failure for
// interactive SSR. Blocking ISR must leave enough time to return its recovery
// page before the platform's 30-second function deadline.
const DETAIL_FETCH_TIMEOUT_MS = 12000;
const DETAIL_FETCH_RETRIES = 1;
const RELATED_APPLICATIONS_FETCH_LIMIT = 250;
const RELATED_APPLICATIONS_FETCH_TIMEOUT_MS = 1800;
const PLANNING_ARTICLE_FETCH_TIMEOUT_MS = 1800;
const OGL_V3_LICENSE_URL = "https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/";
const CITY_SCOPE_ORGANIZATION = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "City Scope",
  url: SITE_URL,
};
const DEFAULT_COUNCIL_ORGANIZATION = {
  "@type": "Organization",
  name: "The City of Edinburgh Council",
  url: "https://www.edinburgh.gov.uk/planning-applications-1/view-comment-planning-applications",
};

let applicationsCache = new Map();

const BOT_USER_AGENT_PATTERN = /(bot|crawler|spider|slurp|facebookexternalhit|bingpreview)/i;
const TERMINAL_APPLICATION_STATUS_PATTERN = /\b(?:decided|determined|approved|granted|permitted|allowed|refused|rejected|withdrawn|completed|not required|certificate issued|consent granted|permission granted|appeal allowed|appeal dismissed|varied)\b/i;

export function getDetailPageRevalidateSeconds(application, nowMs = Date.now()) {
  if (isSourceOnlyPlanningRecord(application)) {
    return DETAIL_PAGE_REVALIDATE_SECONDS;
  }
  const decisionSignals = [
    application?.status,
    application?.decision,
    application?.decision_type,
    application?.decision_status,
    application?.application_status,
    application?.decision_outcome,
  ]
    .filter((value) => typeof value === "string" && value.trim())
    .join(" ");
  const hasDecisionDate = Boolean(application?.decision_date || application?.decisionDate);
  const isTerminal = hasDecisionDate || TERMINAL_APPLICATION_STATUS_PATTERN.test(decisionSignals);

  if (!isTerminal) return DETAIL_PAGE_REVALIDATE_SECONDS;

  const terminalDateValue =
    application?.decision_date ||
    application?.decisionDate ||
    application?.received_date ||
    application?.receivedDate ||
    application?.validated_date ||
    application?.validatedDate ||
    null;
  const terminalDateMs = terminalDateValue ? Date.parse(String(terminalDateValue)) : Number.NaN;
  if (
    Number.isFinite(terminalDateMs) &&
    Number.isFinite(nowMs) &&
    nowMs - terminalDateMs >= DETAIL_PAGE_ARCHIVE_AFTER_MS
  ) {
    return DETAIL_PAGE_ARCHIVED_REVALIDATE_SECONDS;
  }

  return DETAIL_PAGE_DECIDED_REVALIDATE_SECONDS;
}

function createInteractiveFetch(userAgent) {
  if (
    typeof userAgent !== "string" ||
    !userAgent ||
    BOT_USER_AGENT_PATTERN.test(userAgent) ||
    !/mozilla|chrome|safari|firefox|edg|opr/i.test(userAgent) ||
    /^node$/i.test(userAgent.trim())
  ) {
    return undefined;
  }
  return async (input, init) => {
    const headers = new Headers(init?.headers);
    headers.set("User-Agent", "Mozilla/5.0 CityScopeInteractiveDetail");
    headers.set("X-CityScope-Interactive", "browser");
    return fetch(input, { ...init, headers });
  };
}

function createServerDetailFetch(userAgent) {
  return async (input, init) => {
    const headers = new Headers(init?.headers);
    headers.set("User-Agent", String(userAgent || "CityScopeServerDetail/1.0"));
    headers.set("X-CityScope-Interactive", "ssr-detail");
    return fetch(input, { ...init, headers });
  };
}

export function __resetApplicationsCacheForTests() {
  applicationsCache = new Map();
}

function safeDecode(segment) {
  if (typeof segment !== "string") return "";
  try {
    return decodeURIComponent(segment);
  } catch (_) {
    return segment;
  }
}

function referenceSegmentsFromParams(param) {
  const rawSegments = Array.isArray(param) ? param : [param];
  let segments = rawSegments
    .filter((seg) => typeof seg === "string" && seg.length > 0)
    .map((seg) => safeDecode(seg).trim())
    .filter(Boolean);

  if (segments.length === 1 && /^https?:\/\//i.test(segments[0])) {
    try {
      const parsed = new URL(segments[0]);
      segments = parsed.pathname.split("/").map((part) => part.trim()).filter(Boolean);
    } catch (_) {
      // Fall through to token-based cleanup.
    }
  }

  const normalizedSegments = segments
    .filter((seg) => !/^https?:$/i.test(seg))
    .filter((seg) => !/^(?:www\.)?city-scope\.co\.uk$/i.test(seg))
    .filter((seg) => !/^planning-application$/i.test(seg))
    .filter((seg) => !/^planning-updates$/i.test(seg));

  if (normalizedSegments.length > 0) {
    segments = normalizedSegments;
  }

  const documentSegmentIndex = segments.findIndex((seg) => /^document$/i.test(seg));
  if (documentSegmentIndex >= 0) {
    segments = segments.slice(0, documentSegmentIndex);
  }

  if (segments.length === 0) return null;
  return segments;
}

function parseReferenceFromParams(param) {
  let segments = referenceSegmentsFromParams(param);
  if (!segments || segments.length === 0) {
    return { reference: null, councilSlug: null };
  }

  const prefixedCouncil = getCouncilConfig(segments[0]);
  if (prefixedCouncil && segments.length > 1) {
    segments = segments.slice(1);
    return {
      reference: segments.join("/"),
      councilSlug: prefixedCouncil.slug,
    };
  }

  return {
    reference: segments.join("/"),
    councilSlug: null,
  };
}

function queryValues(query, key) {
  const raw = query?.[key];
  if (Array.isArray(raw)) return raw.map((value) => String(value || ""));
  return raw == null ? [] : [String(raw)];
}

function inferCouncilSlugFromDocumentQuery(query = {}) {
  const explicitCouncil = query?.council ? getCouncilConfig(query.council) : null;
  if (explicitCouncil) return explicitCouncil.slug;

  const sourceText = ["url", "app", "preview"]
    .flatMap((key) => queryValues(query, key))
    .join(" ")
    .toLowerCase();

  if (sourceText.includes("planning-applications.midlothian.gov.uk")) {
    return "midlothian";
  }
  if (sourceText.includes("pa.eastlothian.gov.uk")) {
    return "east-lothian";
  }
  if (sourceText.includes("citydev-portal.edinburgh.gov.uk")) {
    return "edinburgh";
  }
  return null;
}

function normalizeReferenceFromParams(param) {
  return parseReferenceFromParams(param).reference;
}

function isValidReference(ref, councilSlug) {
  if (!ref) return false;
  if (isLondonCouncil(councilSlug)) {
    return ref.length <= 120 && /^[A-Za-z0-9][A-Za-z0-9/_.()-]*$/.test(ref);
  }
  // Accept refs like "25/06373/ADV" and other alphanumeric variants.
  return /^[A-Za-z0-9/_.-]+$/.test(ref);
}

function formatIsoDate(value) {
  const date = parseDate(value);
  if (!date) return null;
  return date.toISOString();
}

function trimToLength(value, maxLength) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  const sliced = text.slice(0, Math.max(0, maxLength - 3)).trim();
  const boundary = sliced.lastIndexOf(" ");
  const clean =
    boundary > Math.floor(maxLength * 0.6)
      ? sliced.slice(0, boundary)
      : sliced;
  return `${clean.replace(/[.,;:]+$/, "")}...`;
}

function trimTitlePart(value, maxLength) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  const sliced = text.slice(0, maxLength).trim();
  const boundary = sliced.lastIndexOf(" ");
  const clean =
    boundary > Math.floor(maxLength * 0.6)
      ? sliced.slice(0, boundary)
      : sliced;
  return clean.replace(/[.,;:]+$/, "");
}

function getDocumentHref(doc) {
  if (!doc || typeof doc !== "object") return null;
  return (
    doc?.doc_link ||
    doc?.document_url ||
    doc?.pdf_url ||
    doc?.url ||
    doc?.link ||
    doc?.thumbnail_url ||
    null
  );
}

function getOpenGraphImageType(url) {
  const pathname = String(url || "").split(/[?#]/, 1)[0].toLowerCase();
  if (pathname.endsWith(".png")) return "image/png";
  if (pathname.endsWith(".webp")) return "image/webp";
  if (pathname.endsWith(".gif")) return "image/gif";
  return "image/jpeg";
}

export function getApplicationOpenGraphImage(application, publishedArticle = null) {
  const articleSlug = String(publishedArticle?.slug || "").trim();
  if (articleSlug) {
    return {
      url: `${SITE_URL}/og/blog/${encodeURIComponent(articleSlug)}.png`,
      type: "image/png",
      width: 1200,
      height: 630,
    };
  }

  const documents = Array.isArray(application?.documents) ? application.documents : [];
  const previewUrl = documents
    .map((doc) => doc?.thumbnail_url || doc?.thumbnail || "")
    .map((value) => String(value || "").trim())
    .find((value) => /^https?:\/\//i.test(value));

  if (previewUrl) {
    return {
      url: previewUrl,
      type: getOpenGraphImageType(previewUrl),
      width: null,
      height: null,
    };
  }

  const mapSnapshotUrl = getMapSnapshotUrl(application, {
    width: 1200,
    height: 630,
    zoom: 15,
    absolute: true,
  });
  if (mapSnapshotUrl) {
    return {
      url: mapSnapshotUrl,
      type: "image/jpeg",
      width: 1200,
      height: 630,
    };
  }

  return {
    url: `${SITE_URL}/city-scope-social-v2.jpg`,
    type: "image/jpeg",
    width: 1200,
    height: 630,
  };
}

function getFirstApplicationValue(application, keys) {
  for (const key of keys) {
    const value = application?.[key];
    if (value == null) continue;
    const text = String(value).replace(/\s+/g, " ").trim();
    if (text) return text;
  }
  return "";
}

const CRAWLABLE_DETAIL_FIELDS = [
  { label: "Application type", keys: ["application_type", "type", "applicationType"] },
  { label: "Decision", keys: ["decision", "decision_type", "decisionType"] },
  { label: "Decision date", keys: ["decision_date", "decisionDate"], type: "date" },
  { label: "Ward", keys: ["ward", "ward_name", "wardName"] },
  { label: "Council", keys: ["council", "local_authority", "localAuthority"] },
  { label: "Applicant", keys: ["applicant_name", "applicant", "applicantName"] },
  { label: "Agent", keys: ["agent_name", "agent", "agentName"] },
  { label: "Case officer", keys: ["case_officer", "caseOfficer", "officer"] },
  { label: "Consultation end", keys: ["consultation_end_date", "consultationEndDate"], type: "date" },
  { label: "Neighbour comments", keys: ["neighbour_comments", "neighbourComments", "comments_count"] },
  { label: "Easting", keys: ["easting", "x"] },
  { label: "Northing", keys: ["northing", "y"] },
  { label: "Latitude", keys: ["latitude", "lat"] },
  { label: "Longitude", keys: ["longitude", "lng", "lon"] },
];

function ApplicationFactsContent({ application, relatedApplications = [], publishedArticle = null }) {
  const safeReference = String(application?.reference_no || application?.ref || "").trim();
  const council = getCouncilConfig(application?.source_council) || getCouncilConfig(DEFAULT_COUNCIL_SLUG);
  const qualifiedReference = formatCouncilQualifiedReference(safeReference, council?.slug);
  const hubHref = council && !isLegacyCouncil(council.slug)
    ? `/planning-updates/${council.shortCode}`
    : "/planning-updates";
  const mapHref = council && !isLegacyCouncil(council.slug)
    ? `/planning-map/${council.shortCode}`
    : "/planning-map";
  // Server-rendered links give crawlers a path from this page to its council hub and the council's full archive.
  const archiveHref = council && council.region !== "london" ? buildPlanningArchivePath(1, council.slug) : null;
  const breadcrumbPlace = council ? getPlanningSeoPlaceName(council.slug) : "Planning applications";
  const address = String(application?.address || "").trim();
  const status = String(application?.status || "").trim();
  const category = String(application?.category || "").trim();
  const description = String(application?.description || "").trim();
  const aiSummary = String(application?.ai_summary || "").trim();
  const leadSummary = aiSummary || description;
  const scaleRating =
    typeof application?.scale_rating === "number"
      ? `${application.scale_rating}/10`
      : null;

  const receivedDate = formatLongDate(application?.received_date);
  const validatedDate = formatLongDate(application?.validated_date);
  const scrapedDate = formatLongDate(application?.scraped_date);
  const createdDate = formatLongDate(application?.created_at);
  const councilSource = application?.link ? String(application.link) : null;
  const isSearchSummary = application?.description_completeness === 'official_search_summary';
  const pointPrecision = application?.geocode_metadata?.precision || application?.location?.precision;

  const documents = Array.isArray(application?.documents) ? application.documents : [];
  const availableDocuments = documents.filter((doc) => Boolean(getDocumentHref(doc)));
  const related = Array.isArray(relatedApplications) ? relatedApplications : [];
  const extraDetails = CRAWLABLE_DETAIL_FIELDS
    .map((field) => {
      const rawValue = getFirstApplicationValue(application, field.keys);
      const value = field.type === "date" ? formatLongDate(rawValue) || rawValue : rawValue;
      const isoValue = field.type === "date" ? formatIsoDate(rawValue) : null;
      return value ? { ...field, value, isoValue } : null;
    })
    .filter(Boolean);

  const councilName = council?.officialName || council?.displayName || "Local authority";

  return (
    <section
      className="planning-register border-t border-[var(--c-border)] bg-[var(--c-bg)] px-4 pb-20 pt-10 text-[var(--c-text-primary)] sm:px-6"
      aria-labelledby="planning-application-overview-title"
      data-seo-static-content="true"
      itemScope
      itemType="https://schema.org/Thing"
    >
      <article className="mx-auto w-full max-w-5xl">
        {/* Masthead */}
        <header>
          <nav aria-label="Breadcrumb" className="pr-data mb-4 flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link href="/" className="pr-textlink">Home</Link>
            <span aria-hidden="true">&rsaquo;</span>
            <Link href="/planning-updates" className="pr-textlink">Planning updates</Link>
            <span aria-hidden="true">&rsaquo;</span>
            <Link href={hubHref} className="pr-textlink">{breadcrumbPlace}</Link>
            <span aria-hidden="true">&rsaquo;</span>
            <span aria-current="page">{qualifiedReference}</span>
          </nav>
          <p className="pr-kicker pr-kicker--accent">
            CityScope &middot; Planning Updates
          </p>
          <h1
            id="planning-application-overview-title"
            className="pr-masthead mt-4 !text-[clamp(30px,4.4vw,52px)]"
          >
            Application <em>{qualifiedReference}</em>
          </h1>
          <meta itemProp="identifier" content={safeReference} />
          {address ? (
            <p
              className="mt-3 max-w-3xl font-['Newsreader'] text-xl italic leading-snug text-[var(--c-text-secondary)] sm:text-2xl"
              itemProp="spatialCoverage"
            >
              {address}
            </p>
          ) : null}

          <div className="pr-data mt-5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {status ? (
              <span className="pr-status">
                <span className="pr-status-dot bg-[var(--c-accent)]" aria-hidden="true" />
                {status}
              </span>
            ) : null}
            {category ? (
              <>
                <span aria-hidden="true">&middot;</span>
                <span>{category}</span>
              </>
            ) : null}
            {receivedDate ? (
              <>
                <span aria-hidden="true">&middot;</span>
                <span>
                  Received{" "}
                  <time dateTime={formatIsoDate(application?.received_date) || undefined}>{receivedDate}</time>
                </span>
              </>
            ) : null}
            <span aria-hidden="true">&middot;</span>
            <span>{councilName}</span>
          </div>

          <hr className="pr-double-rule mt-6" aria-hidden="true" />
        </header>

        {/* Lead summary */}
        <p
          className="mt-6 max-w-3xl text-[15px] leading-7 text-[var(--c-text-secondary)]"
          itemProp="description"
        >
          {leadSummary || "Key dates, status, related applications, and supporting documents."}
        </p>

        {/* Record + timeline ledger */}
        <div className="mt-10 grid gap-x-12 gap-y-10 md:grid-cols-2">
          <section>
            <h2 className="pr-section-label">The record</h2>
            <dl className="mt-4">
              <div className="pr-fact">
                <dt>Reference</dt>
                <dd className="pr-data !text-[var(--c-text-primary)]">{qualifiedReference}</dd>
              </div>
              {address ? (
                <div className="pr-fact">
                  <dt>Address</dt>
                  <dd>{address}</dd>
                </div>
              ) : null}
              {status ? (
                <div className="pr-fact">
                  <dt>Status</dt>
                  <dd>{status}</dd>
                </div>
              ) : null}
              <CouncilDecisionFacts application={application} />
              {category ? (
                <div className="pr-fact">
                  <dt>Category</dt>
                  <dd>{category}</dd>
                </div>
              ) : null}
              {scaleRating ? (
                <div className="pr-fact">
                  <dt>Impact scale</dt>
                  <dd className="pr-data !text-[var(--c-text-primary)]">{scaleRating}</dd>
                </div>
              ) : null}
              <div className="pr-fact">
                <dt>Authority</dt>
                <dd>{councilName}</dd>
              </div>
            </dl>
          </section>

          <section>
            <h2 className="pr-section-label">Timeline</h2>
            <dl className="mt-4">
              {receivedDate ? (
                <div className="pr-fact">
                  <dt>Received</dt>
                  <dd>
                    <time dateTime={formatIsoDate(application?.received_date) || undefined}>{receivedDate}</time>
                  </dd>
                </div>
              ) : null}
              {validatedDate ? (
                <div className="pr-fact">
                  <dt>Validated</dt>
                  <dd>
                    <time dateTime={formatIsoDate(application?.validated_date) || undefined}>{validatedDate}</time>
                  </dd>
                </div>
              ) : null}
              {scrapedDate ? (
                <div className="pr-fact">
                  <dt>Last scraped</dt>
                  <dd>
                    <time dateTime={formatIsoDate(application?.scraped_date) || undefined}>{scrapedDate}</time>
                  </dd>
                </div>
              ) : null}
              {createdDate ? (
                <div className="pr-fact">
                  <dt>Record created</dt>
                  <dd>
                    <time dateTime={formatIsoDate(application?.created_at) || undefined}>{createdDate}</time>
                  </dd>
                </div>
              ) : null}
            </dl>
          </section>
        </div>

        {extraDetails.length > 0 ? (
          <section className="mt-10">
            <h2 className="pr-section-label">Additional application data</h2>
            <dl className="mt-4 grid gap-x-12 md:grid-cols-2">
              {extraDetails.map((detail) => (
                <div key={detail.label} className="pr-fact">
                  <dt>{detail.label}</dt>
                  <dd>
                    {detail.isoValue ? <time dateTime={detail.isoValue}>{detail.value}</time> : detail.value}
                  </dd>
                </div>
              ))}
            </dl>
            {application?.source_record_only && pointPrecision ? (
              <p className="mt-3 text-sm text-[var(--c-text-secondary)]">
                {/approximate|postcode/i.test(pointPrecision) ? 'Approximate location.' : 'Source location point.'} The exact site boundary has not been verified.
              </p>
            ) : null}
          </section>
        ) : null}

        {aiSummary || description ? (
          <section className="mt-10">
            <h2 className="pr-section-label">What is proposed</h2>
            {aiSummary ? (
              <div className="mt-4">
                <p className="pr-kicker text-[var(--c-accent)]">Plain-language summary</p>
                <p className="mt-2 max-w-3xl whitespace-pre-wrap text-[15px] leading-7 text-[var(--c-text-primary)]">
                  {aiSummary}
                </p>
              </div>
            ) : null}
            {description ? (
              <div className={aiSummary ? "mt-6 border-t border-[var(--c-border)] pt-5" : "mt-4"}>
                <p className="pr-kicker">{isSearchSummary ? 'Official search summary' : 'As filed with the council'}</p>
                {isSearchSummary ? <p className="mt-2 text-sm text-[var(--c-text-secondary)]">The source search result may shorten the proposal. Check the source record for its full wording.</p> : null}
                <p className="mt-2 max-w-3xl whitespace-pre-wrap text-[14px] leading-7 text-[var(--c-text-secondary)]">
                  {description}
                </p>
              </div>
            ) : null}
          </section>
        ) : null}

        {related.length > 0 ? (
          <section className="mt-10">
            <h2 className="pr-section-label">
              Related applications
              <span className="pr-data normal-case tracking-normal">({related.length})</span>
            </h2>
            <ul className="mt-2">
              {related.map((item, index) => {
                const relatedRef = String(item?.reference_no || item?.ref || "").trim();
                const relatedQualifiedRef = formatCouncilQualifiedReference(
                  relatedRef,
                  item?.source_council || application?.source_council
                );
                const relatedHref = buildPlanningApplicationPath(
                  relatedRef,
                  item?.source_council || application?.source_council
                );
                const relatedStatus = String(item?.status || "").trim();
                const relatedDate = formatLongDate(item?.received_date || item?.scraped_date || item?.created_at);
                const relatedSummary = String(item?.ai_summary || item?.description || "").trim();

                return (
                  <li key={`${relatedRef}-${index}`} className="pr-ledger-row !py-4">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <Link
                        href={relatedHref}
                        prefetch={false}
                        className="pr-data inline-flex min-h-6 items-center !text-[var(--c-text-primary)] underline decoration-[var(--c-border)] underline-offset-4 transition-colors hover:!text-[var(--c-accent)] hover:decoration-current"
                      >
                        {relatedQualifiedRef}
                      </Link>
                      {relatedStatus ? (
                        <span className="pr-data">{relatedStatus}</span>
                      ) : null}
                      {relatedDate ? (
                        <span className="pr-data text-[var(--c-text-tertiary)]">{relatedDate}</span>
                      ) : null}
                    </div>
                    {relatedSummary ? (
                      <p className="mt-1.5 max-w-3xl text-[13px] leading-6 text-[var(--c-text-secondary)]">
                        {trimToLength(relatedSummary, 220)}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {availableDocuments.length > 0 ? (
          <section className="mt-10">
            <h2 className="pr-section-label">
              Supporting documents
              <span className="pr-data normal-case tracking-normal">({availableDocuments.length})</span>
            </h2>
            <ul className="mt-2">
              {availableDocuments.map((doc, index) => {
                const href = getDocumentHref(doc);
                const role = String(doc?.role || "").trim();
                const title = String(doc?.title || doc?.name || doc?.filename || "").trim();
                const documentDate = formatLongDate(doc?.date || doc?.created_at || doc?.published_at);
                const label = role || title || `Document ${index + 1}`;
                return (
                  <li
                    key={`${href}-${index}`}
                    className="pr-ledger-row flex flex-wrap items-baseline gap-x-3 gap-y-1 !py-3.5"
                    itemScope
                    itemType="https://schema.org/DigitalDocument"
                  >
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer nofollow external"
                      onClick={(event) => {
                        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
                          return;
                        }
                        event.preventDefault();
                        openPlanningDoc(href, application?.link || application?.portal_url || "", doc, undefined, {
                          reference: application?.reference_no || application?.ref || "",
                          title,
                          sourceDesc: role,
                          previewUrl: doc?.thumbnail_url || doc?.thumbnail,
                        });
                      }}
                      className="inline-flex min-h-6 items-center text-[14px] font-medium text-[var(--c-text-primary)] underline decoration-[var(--c-border)] underline-offset-4 transition-colors hover:text-[var(--c-accent)] hover:decoration-current"
                      itemProp="url"
                    >
                      <span itemProp="name">{label}</span>
                    </a>
                    {title && title !== label ? (
                      <span className="text-[13px] text-[var(--c-text-secondary)]">{title}</span>
                    ) : null}
                    {documentDate ? (
                      <span className="pr-data text-[var(--c-text-tertiary)]">
                        <time
                          dateTime={formatIsoDate(doc?.date || doc?.created_at || doc?.published_at) || undefined}
                          itemProp="datePublished"
                        >
                          {documentDate}
                        </time>
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {publishedArticle ? (
          <section className="pr-panel mt-12 border-t-2 !border-t-[var(--c-text-primary)] p-6">
            <p className="pr-kicker pr-kicker--accent">City Scope Journal</p>
            <h2 className="pr-heading mt-3">{publishedArticle.title}</h2>
            {publishedArticle.excerpt ? (
              <p className="mt-3 max-w-3xl text-[14px] leading-7 text-[var(--c-text-secondary)]">
                {publishedArticle.excerpt}
              </p>
            ) : null}
            <Link href={`/blog/${publishedArticle.slug}`} className="pr-textlink mt-4">
              Read the Journal article &rarr;
            </Link>
          </section>
        ) : null}

        <hr className="pr-rule-strong mt-12" aria-hidden="true" />
        <nav className="mt-5 flex flex-wrap items-center gap-3">
          <Link href={hubHref} className="pr-button pr-button--ghost">
            Back to Planning Updates
          </Link>
          <Link href={mapHref} className="pr-button pr-button--ghost">
            Explore Planning Map
          </Link>
          {archiveHref ? (
            <Link href={archiveHref} className="pr-button pr-button--ghost">
              Browse all {breadcrumbPlace} applications
            </Link>
          ) : null}
          {councilSource ? (
            <a href={councilSource} target="_blank" rel="noopener noreferrer" className="pr-textlink ml-1">
              Council source &#8599;
            </a>
          ) : null}
        </nav>
      </article>
    </section>
  );
}

export function buildApplicationSeoPayload(application, canonicalPath, publishedArticle = null) {
  const safeRef = String(application?.reference_no || application?.ref || "").trim();
  const council = getCouncilConfig(application?.source_council) || getCouncilConfig(DEFAULT_COUNCIL_SLUG);
  const placeName = getPlanningSeoPlaceName(council?.slug);
  const authorityName = getPlanningSeoAuthorityName(council?.slug);
  const planningUpdatesUrl = council && !isLegacyCouncil(council.slug)
    ? `${SITE_URL}/planning-updates/${council.shortCode}`
    : `${SITE_URL}/planning-updates`;
  const councilOrganization = council
    ? {
        "@type": "Organization",
        additionalType: "https://schema.org/GovernmentOrganization",
        name: council.officialName,
        url: council.publicSearchUrl || council.portalBaseUrl,
        identifier: council.authorityCode,
      }
    : DEFAULT_COUNCIL_ORGANIZATION;
  const address = trimToLength(application?.address || "", 90);
  const status = trimToLength(application?.status || "", 50);
  const category = trimToLength(application?.category || "", 60);
  const aiSummary = trimToLength(application?.ai_summary || "", 220);
  const description = trimToLength(application?.description || "", 240);
  const receivedLabel = formatLongDate(application?.received_date);
  const modifiedIso =
    formatIsoDate(application?.scraped_date) ||
    formatIsoDate(application?.received_date) ||
    formatIsoDate(application?.validated_date) ||
    formatIsoDate(application?.created_at) ||
    null;
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const proposalText = description || aiSummary;
  const documents = Array.isArray(application?.documents) ? application.documents : [];
  const documentItems = documents
    .map((doc, index) => {
      const href = getDocumentHref(doc);
      if (!href) return null;
      const name = trimToLength(doc?.role || doc?.title || doc?.name || doc?.filename || `Document ${index + 1}`, 100);
      return {
        "@type": "DigitalDocument",
        "@id": `${canonicalUrl}#document-${index + 1}`,
        name,
        url: href,
        ...(formatIsoDate(doc?.date || doc?.created_at || doc?.published_at)
          ? { datePublished: formatIsoDate(doc?.date || doc?.created_at || doc?.published_at) }
          : {}),
      };
    })
    .filter(Boolean);

  const titleParts = [`${placeName} planning application ${safeRef}`];
  if (address) titleParts.push(trimTitlePart(address, 58));
  if (!address && category) titleParts.push(category);
  if (status) titleParts.push(status);
  const title = `${titleParts.join(" | ")} | City Scope`;

  const compactAddress = trimTitlePart(address, 72);
  const compactProposal = trimToLength(proposalText, 82);
  const descriptionParts = [
    compactAddress
      ? `${placeName} planning application ${safeRef} for ${compactAddress}`
      : `${placeName} planning application ${safeRef}`,
  ];
  if (status) descriptionParts.push(`Status: ${status}`);
  if (compactProposal) descriptionParts.push(`Proposal: ${compactProposal}`);
  if (category && !compactProposal) descriptionParts.push(`Category: ${category}`);
  if (authorityName) descriptionParts.push(`Source: ${authorityName}`);
  if (receivedLabel) descriptionParts.push(`Received ${receivedLabel}`);
  const metaDescription = trimToLength(descriptionParts.join(". "), 180);

  const keywordTerms = [
    `${placeName} planning applications`,
    `${authorityName} planning applications`,
    `planning application ${safeRef}`,
    safeRef,
    address,
    status,
    category,
    "planning permission Scotland",
    "City Scope planning applications",
  ]
    .filter(Boolean)
    .join(", ");

  const additionalProperty = [
    status
      ? {
          "@type": "PropertyValue",
          name: "Application status",
          value: status,
        }
      : null,
    category
      ? {
          "@type": "PropertyValue",
          name: "Application category",
          value: category,
        }
      : null,
    typeof application?.scale_rating === "number"
      ? {
          "@type": "PropertyValue",
          name: "Impact scale rating",
          value: `${application.scale_rating}/10`,
        }
      : null,
    receivedLabel
      ? {
          "@type": "PropertyValue",
          name: "Received date",
          value: receivedLabel,
        }
      : null,
  ].filter(Boolean);
  const publishedArticleUrl = publishedArticle?.slug
    ? `${SITE_URL}/blog/${publishedArticle.slug}`
    : null;
  const articleSeoTitle = trimToLength(
    publishedArticle?.seo?.seoTitle || publishedArticle?.title || "",
    92
  );
  const articleSeoDescription = trimToLength(
    publishedArticle?.seo?.metaDescription || publishedArticle?.excerpt || "",
    180
  );
  const pageTitle = publishedArticle
    ? `${articleSeoTitle || publishedArticle.title} | City Scope`
    : title;
  const pageDescription = publishedArticle
    ? articleSeoDescription || metaDescription
    : metaDescription;
  const publishedArticleNode = publishedArticleUrl
    ? {
        "@type": "BlogPosting",
        "@id": `${publishedArticleUrl}#article`,
        headline: publishedArticle.title,
        description: publishedArticle.excerpt,
        url: publishedArticleUrl,
        datePublished: publishedArticle.publishedAt,
        dateModified: publishedArticle.updatedAt || publishedArticle.publishedAt,
        author: {
          "@type": "Organization",
          name: "City Scope",
          url: SITE_URL,
        },
        publisher: CITY_SCOPE_ORGANIZATION,
        about: {
          "@id": `${canonicalUrl}#application`,
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${publishedArticleUrl}#webpage`,
        },
      }
    : null;

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Thing",
        "@id": `${canonicalUrl}#application`,
        name: `${placeName} planning application ${safeRef}`,
        identifier: safeRef,
        description: proposalText || metaDescription,
        url: canonicalUrl,
        ...(address ? { locationCreated: { "@type": "Place", name: address } } : {}),
        ...(additionalProperty.length ? { additionalProperty } : {}),
        ...(application?.link ? { sameAs: [application.link] } : {}),
        ...(documentItems.length || publishedArticleNode
          ? {
              subjectOf: [
                ...documentItems.map((doc) => ({ "@id": doc["@id"] })),
                ...(publishedArticleNode ? [{ "@id": publishedArticleNode["@id"] }] : []),
              ],
            }
          : {}),
      },
      {
        "@type": "Dataset",
        "@id": `${canonicalUrl}#dataset`,
        name: `${placeName} planning application data ${safeRef}`,
        description: metaDescription,
        url: canonicalUrl,
        identifier: safeRef,
        inLanguage: "en-GB",
        keywords: keywordTerms,
        creator: CITY_SCOPE_ORGANIZATION,
        publisher: CITY_SCOPE_ORGANIZATION,
        provider: councilOrganization,
        sourceOrganization: councilOrganization,
        license: {
          "@type": "CreativeWork",
          name: "Open Government Licence v3.0",
          url: OGL_V3_LICENSE_URL,
        },
        isBasedOn: application?.link || canonicalUrl,
        mainEntity: { "@id": `${canonicalUrl}#application` },
      },
      {
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: pageTitle,
        description: metaDescription,
        inLanguage: "en-GB",
        isPartOf: { "@id": `${SITE_URL}/#website` },
        mainEntity: { "@id": `${canonicalUrl}#application` },
        ...(publishedArticleNode ? { relatedLink: [publishedArticleUrl] } : {}),
        ...(modifiedIso ? { dateModified: modifiedIso } : {}),
      },
      ...documentItems,
      ...(publishedArticleNode ? [publishedArticleNode] : []),
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: `${placeName} planning applications`,
            item: planningUpdatesUrl,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: safeRef || "Application",
            item: canonicalUrl,
          },
        ],
      },
    ],
  };

  return {
    title: pageTitle,
    description: pageDescription,
    keywords: keywordTerms,
    structuredData,
  };
}

function normalizeApplicationRecord(app) {
  if (!app || typeof app !== "object") return null;
  app = sanitizePublicPlanningRecord(app);

  const documents = Array.isArray(app.documents) ? app.documents : [];
  const normalizedDocuments = documents
    .filter((doc) => doc && typeof doc === "object")
    .map((doc) => {
      const normalizedDoc = { ...doc };
      if (doc?.role != null) normalizedDoc.role = String(doc.role);
      if (doc?.doc_link != null) normalizedDoc.doc_link = String(doc.doc_link);
      if (doc?.pdf_url != null) normalizedDoc.pdf_url = String(doc.pdf_url);
      if (doc?.thumbnail_url != null) normalizedDoc.thumbnail_url = String(doc.thumbnail_url);
      if (doc?.document_url != null) normalizedDoc.document_url = String(doc.document_url);
      const documentHref = getDocumentHref(normalizedDoc);
      if (documentHref) {
        normalizedDoc.document_url = String(normalizedDoc.document_url || documentHref);
        normalizedDoc.doc_link = String(normalizedDoc.doc_link || documentHref);
      }
      return normalizedDoc;
    });

  return {
    ...app,
    application_uid: app?.application_uid ? String(app.application_uid) : "",
    source_council: app?.source_council ? String(app.source_council) : DEFAULT_COUNCIL_SLUG,
    source_system: app?.source_system ? String(app.source_system) : "idox",
    council_name: app?.council_name ? String(app.council_name) : "",
    council_code: app?.council_code ? String(app.council_code) : "",
    portal_url: app?.portal_url ? String(app.portal_url) : "",
    reference_no: app?.reference_no ? String(app.reference_no) : "",
    ref: app?.ref ? String(app.ref) : "",
    address: app?.address ? String(app.address) : "",
    description: app?.description ? String(app.description) : "",
    ai_summary: app?.ai_summary ? String(app.ai_summary) : "",
    status: app?.status ? String(app.status) : "",
    category: app?.category ? String(app.category) : "",
    link: app?.link ? String(app.link) : "",
    application_type: app?.application_type ? String(app.application_type) : "",
    type: app?.type ? String(app.type) : "",
    decision: app?.decision ? String(app.decision) : "",
    decision_type: app?.decision_type ? String(app.decision_type) : "",
    decision_date: app?.decision_date ?? null,
    ward: app?.ward ? String(app.ward) : "",
    ward_name: app?.ward_name ? String(app.ward_name) : "",
    council: app?.council ? String(app.council) : "",
    local_authority: app?.local_authority ? String(app.local_authority) : "",
    applicant_name: app?.applicant_name ? String(app.applicant_name) : "",
    applicant: app?.applicant ? String(app.applicant) : "",
    agent_name: app?.agent_name ? String(app.agent_name) : "",
    agent: app?.agent ? String(app.agent) : "",
    case_officer: app?.case_officer ? String(app.case_officer) : "",
    consultation_end_date: app?.consultation_end_date ?? null,
    neighbour_comments: app?.neighbour_comments ?? app?.neighbourComments ?? app?.comments_count ?? null,
    easting: app?.easting ?? app?.x ?? null,
    northing: app?.northing ?? app?.y ?? null,
    latitude: app?.latitude ?? app?.lat ?? null,
    longitude: app?.longitude ?? app?.lng ?? app?.lon ?? null,
    group_id: app?.group_id ?? null,
    received_date: app?.received_date ?? null,
    validated_date: app?.validated_date ?? null,
    scraped_date: app?.scraped_date ?? null,
    created_at: app?.created_at ?? null,
    scale_rating:
      typeof app?.scale_rating === "number" ? app.scale_rating : Number(app?.scale_rating) || null,
    documents: normalizedDocuments,
  };
}

function normalizeAddressForGrouping(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^(flat|apartment|apt|unit)\s+\d+[a-z]*,?\s*/i, "")
    .replace(/^\d+[a-z]*[f/-]\d+[a-z]*,?\s*/i, "")
    .replace(/^pf\d+\s*,?\s*/i, "");
}

function buildRelatedApplicationsForStaticContent(currentApp, allApplications) {
  if (!currentApp || !Array.isArray(allApplications) || allApplications.length === 0) {
    return [];
  }

  const currentReference = applicationIdentityKey(currentApp);
  if (!currentReference) return [];

  let related = [];

  if (currentApp.group_id) {
    related = allApplications.filter(
      (item) => item?.group_id && String(item.group_id) === String(currentApp.group_id)
    );
  }

  if (related.length === 0) {
    const baseAddress = normalizeAddressForGrouping(currentApp.address || currentApp.location || "");
    if (baseAddress) {
      related = allApplications.filter((item) => {
        const candidateAddress = normalizeAddressForGrouping(item?.address || item?.location || "");
        return candidateAddress && candidateAddress === baseAddress;
      });
    }
  }

  const deduped = [];
  const seen = new Set();
  for (const item of related) {
    const normalized = normalizeApplicationRecord(item);
    if (!normalized) continue;
    const ref = applicationIdentityKey(normalized);
    if (!ref || ref === currentReference || seen.has(ref)) continue;
    seen.add(ref);
    deduped.push(normalized);
  }

  deduped.sort((a, b) => {
    const aTime = parseDate(a?.received_date || a?.scraped_date || a?.created_at)?.getTime() || 0;
    const bTime = parseDate(b?.received_date || b?.scraped_date || b?.created_at)?.getTime() || 0;
    return bTime - aTime;
  });

  return deduped.slice(0, 12);
}

async function fetchApplicationsList(councilSlug = DEFAULT_COUNCIL_SLUG) {
  const councilKey = getCouncilConfig(councilSlug)?.slug || DEFAULT_COUNCIL_SLUG;
  const cacheEntry = applicationsCache.get(councilKey) || {
    fetchedAt: 0,
    data: null,
    inFlight: null,
  };
  const now = Date.now();
  if (cacheEntry.data && now - cacheEntry.fetchedAt < CACHE_TTL_MS) {
    return cacheEntry.data;
  }

  if (cacheEntry.inFlight) {
    return cacheEntry.inFlight;
  }

  cacheEntry.inFlight = fetchApplicationsApi({
    limit: RELATED_APPLICATIONS_FETCH_LIMIT,
    retries: 0,
    timeoutMs: RELATED_APPLICATIONS_FETCH_TIMEOUT_MS,
    council: councilKey,
  })
    .finally(() => {
      cacheEntry.inFlight = null;
    });
  applicationsCache.set(councilKey, cacheEntry);

  const data = await cacheEntry.inFlight;
  cacheEntry.data = data;
  cacheEntry.fetchedAt = Date.now();
  applicationsCache.set(councilKey, cacheEntry);
  return data;
}

function setDetailPageCacheHeader(
  res,
  {
    sMaxage = DETAIL_PAGE_S_MAXAGE_SECONDS,
    staleWhileRevalidate = DETAIL_PAGE_STALE_WHILE_REVALIDATE_SECONDS,
  } = {}
) {
  if (!res || typeof res.setHeader !== "function") return;
  res.setHeader(
    "Cache-Control",
    `public, s-maxage=${sMaxage}, stale-while-revalidate=${staleWhileRevalidate}`
  );
}

function PlanningApplicationInlineExperience({
  application,
  hubHref,
  relatedApplications = [],
  publishedArticle = null,
  publishedArticleLookupComplete = false,
}) {
  const router = useRouter();
  const { showAiSummaries, favorites, toggleFavorite } = usePlanningContext() || {};
  const [selectedImage, setSelectedImage] = useState(null);
  const [hydratedFacts, setHydratedFacts] = useState(null);
  const factsApplication = hydratedFacts && applicationIdentityKey(hydratedFacts) === applicationIdentityKey(application)
    ? hydratedFacts : application;

  const safeReference = application?.reference_no || application?.ref || "";
  const requestedTab = typeof router.query?.tab === "string" ? router.query.tab.toLowerCase() : "";
  const initialTab = requestedTab === "discussion"
    ? "discussion"
    : requestedTab === "journal"
      ? "journal"
    : requestedTab === "tags" || requestedTab === "signals"
      ? "signals"
      : "overview";

  const isFavorite = useMemo(() => {
    if (!Array.isArray(favorites) || !safeReference) return false;
    const currentKey = applicationIdentityKey(application || {});
    return favorites.some((fav) => {
      return applicationIdentityKey(fav || {}) === currentKey;
    });
  }, [application, favorites, safeReference]);

  const handleClose = useCallback(() => {
    router.push(hubHref);
  }, [router, hubHref]);

  const handleNavigateApplication = useCallback((nextApplication) => {
    const nextReference = nextApplication?.reference_no || nextApplication?.ref || "";
    if (!nextReference) return;
    const nextHref = buildPlanningApplicationPath(
      nextReference,
      nextApplication?.source_council || application?.source_council
    );
    if (nextHref === router.asPath) return;
    router.push(nextHref);
  }, [application?.source_council, router]);

  const handleToggleFavorite = useCallback(() => {
    if (typeof toggleFavorite === "function") {
      toggleFavorite(application);
    }
  }, [toggleFavorite, application]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.dispatchEvent(new CustomEvent("navbar-visibility", { detail: { visible: true, lock: false } }));
    return undefined;
  }, []);

  return (
    <>
      <div className="planning-register min-h-screen bg-[var(--c-bg)] text-[var(--c-text-primary)]">
        <PlanningApplicationModal
          isOpen
          renderInline
          onClose={handleClose}
          application={application}
          showAiSummaries={showAiSummaries !== false}
          onImageClick={setSelectedImage}
          isFavorite={isFavorite}
          onToggleFavorite={typeof toggleFavorite === "function" ? handleToggleFavorite : undefined}
          onNavigateApplication={handleNavigateApplication}
          onDetailHydrated={setHydratedFacts}
          initialTab={initialTab}
          publishedArticle={publishedArticle}
          publishedArticleLookupComplete={publishedArticleLookupComplete}
        />
      </div>
      <ApplicationFactsContent
        application={factsApplication}
        relatedApplications={relatedApplications}
        publishedArticle={publishedArticle}
      />
      {selectedImage ? <ImageModal image={selectedImage} onClose={() => setSelectedImage(null)} /> : null}
    </>
  );
}

export default function PlanningApplicationPage({
  application = null,
  relatedApplications = [],
  publishedArticle = null,
  publishedArticleLookupComplete = false,
  reference = null,
  error = null,
  councilSlug = DEFAULT_COUNCIL_SLUG,
}) {
  const ref = application?.reference_no || reference || "";
  const pageCouncil = getCouncilConfig(application?.source_council || councilSlug) || getCouncilConfig(DEFAULT_COUNCIL_SLUG);
  const qualifiedRef = formatCouncilQualifiedReference(ref, pageCouncil?.slug);
  const canonicalPath = buildPlanningApplicationPath(ref, pageCouncil?.slug);
  const planningHubHref = pageCouncil && !isLegacyCouncil(pageCouncil.slug)
    ? `/planning-updates/${pageCouncil.shortCode}`
    : "/planning-updates";
  const planningMapHref = pageCouncil && !isLegacyCouncil(pageCouncil.slug)
    ? `/planning-map/${pageCouncil.shortCode}`
    : "/planning-map";

  if (error) {
    return (
      <>
        <Meta
          title={`Planning Application ${ref || ""} | City Scope`}
          description="Planning application details are temporarily unavailable."
          canonical={canonicalPath}
          noindex
          nofollow
        />
        <main className="planning-register min-h-screen bg-[var(--c-bg)] px-4 py-20 text-[var(--c-text-primary)]">
          <div className="mx-auto w-full max-w-3xl">
            <p className="pr-kicker pr-kicker--accent">CityScope &middot; Planning Updates</p>
            <h1 className="pr-heading mt-4 !text-[clamp(26px,3.4vw,38px)]">
              Record temporarily unavailable
            </h1>
            {ref ? <p className="pr-data mt-3">Reference {qualifiedRef}</p> : null}
            <hr className="pr-double-rule mt-6" aria-hidden="true" />
            <p className="mt-6 max-w-xl text-[15px] leading-7 text-[var(--c-text-secondary)]">
              This page could not load the current planning application data. Please try again in a minute.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link href={planningHubHref} className="pr-button">
                Planning Updates
              </Link>
              <Link href={planningMapHref} className="pr-button pr-button--ghost">
                Planning Map
              </Link>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (!application) {
    return (
      <>
        <Meta
          title={`Planning Application ${ref || ""} | City Scope`}
          description="Planning application details."
          canonical={canonicalPath}
          noindex
          nofollow
        />
        <main className="planning-register min-h-screen bg-[var(--c-bg)] px-4 py-20 text-[var(--c-text-primary)]">
          <div className="mx-auto w-full max-w-3xl">
            <p className="pr-kicker pr-kicker--accent">CityScope &middot; Planning Updates</p>
            <h1 className="pr-heading mt-4 !text-[clamp(26px,3.4vw,38px)]">
              No entry found in Planning Updates
            </h1>
            {ref ? <p className="pr-data mt-3">Reference {qualifiedRef}</p> : null}
            <hr className="pr-double-rule mt-6" aria-hidden="true" />
            <p className="mt-6 max-w-xl text-[15px] leading-7 text-[var(--c-text-secondary)]">
              We could not find this reference in the current dataset.
            </p>
            <div className="mt-8">
              <Link href={planningHubHref} className="pr-button">
                Go to Planning Updates
              </Link>
            </div>
          </div>
        </main>
      </>
    );
  }

  const normalizedApplication = normalizeApplicationRecord(application);
  const normalizedRelatedApplications = Array.isArray(relatedApplications)
    ? relatedApplications.map(normalizeApplicationRecord).filter(Boolean)
    : [];
  const safeRef = normalizedApplication?.reference_no || normalizedApplication?.ref || ref;
  const normalizedCouncil = getCouncilConfig(normalizedApplication?.source_council) || pageCouncil;
  const canonicalPathForPage = buildPlanningApplicationPath(safeRef, normalizedCouncil?.slug);
  const hubHref = normalizedCouncil && !isLegacyCouncil(normalizedCouncil.slug)
    ? `/planning-updates/${normalizedCouncil.shortCode}`
    : "/planning-updates";
  const initialApplications = normalizedApplication
    ? [normalizedApplication, ...normalizedRelatedApplications].filter((item, index, arr) => {
        const refValue = applicationIdentityKey(item || {});
        if (!refValue) return false;
        return arr.findIndex((candidate) => {
          const candidateRef = applicationIdentityKey(candidate || {});
          return candidateRef === refValue;
        }) === index;
      })
    : [];
  const publishedArticleForProps = publishedArticle
    ? {
        ...publishedArticle,
        sections: parseMarkdownToBlogSections(publishedArticle.markdown || ""),
      }
    : null;
  const seoPayload = buildApplicationSeoPayload(
    normalizedApplication,
    canonicalPathForPage,
    publishedArticleForProps
  );
  const openGraphImage = getApplicationOpenGraphImage(
    normalizedApplication,
    publishedArticleForProps
  );

  return (
    <>
      <Meta
        title={seoPayload.title}
        description={seoPayload.description}
        keywords={seoPayload.keywords}
        canonical={canonicalPathForPage}
        ogImage={openGraphImage.url}
        ogImageAlt={`${seoPayload.title} - City Scope planning application preview`}
        ogImageType={openGraphImage.type}
        ogImageWidth={openGraphImage.width}
        ogImageHeight={openGraphImage.height}
        noindex={Boolean(normalizedCouncil && !normalizedCouncil.publicEnabled)}
        nofollow={Boolean(normalizedCouncil && !normalizedCouncil.publicEnabled)}
        structuredData={seoPayload.structuredData}
      />

      <PlanningContextProvider
        initialApplications={initialApplications}
        disableDataBootstrap
        disableFeedSummary
        suppressLegacyShortLetHydration={isLondonCouncil(normalizedCouncil?.slug)}
        councilSlug={normalizedCouncil?.slug}
      >
        <>
          <PlanningApplicationInlineExperience
            application={normalizedApplication}
            hubHref={hubHref}
            relatedApplications={normalizedRelatedApplications}
            publishedArticle={publishedArticleForProps}
            publishedArticleLookupComplete={publishedArticleLookupComplete}
          />
        </>
      </PlanningContextProvider>
    </>
  );
}

export async function loadPlanningApplicationPageProps({ params, query = {}, res, req, isIsr = false }) {
  const parsedReference = parseReferenceFromParams(params?.reference);
  const reference = parsedReference.reference;
  const queryCouncil = query?.council ? getCouncilConfig(query.council)?.slug : null;
  const requestedCouncil =
    parsedReference.councilSlug ||
    queryCouncil ||
    inferCouncilSlugFromDocumentQuery(query) ||
    DEFAULT_COUNCIL_SLUG;
  if (!reference || !isValidReference(reference, requestedCouncil)) {
    if (res) {
      res.statusCode = 404;
      setDetailPageCacheHeader(res, {
        sMaxage: DETAIL_PAGE_NOT_FOUND_S_MAXAGE_SECONDS,
        staleWhileRevalidate: DETAIL_PAGE_S_MAXAGE_SECONDS,
      });
    }
    return {
      props: {
        reference: reference || null,
        councilSlug: requestedCouncil,
      },
    };
  }

  const interactiveFetch = createInteractiveFetch(req?.headers?.["user-agent"]);
  const isAutomatedDetailRequest = Boolean(req?.headers) && !interactiveFetch;
  let application;
  try {
    // Resolve the exact reference via the detail endpoint. The feed endpoint is
    // paginated in production, so using it as a lookup source causes false 404s
    // for valid references outside the first page.
    const detailFetch = interactiveFetch || createServerDetailFetch(req?.headers?.["user-agent"]);
    application = await fetchApplicationById(reference, {
      retries: isIsr ? 0 : DETAIL_FETCH_RETRIES,
      timeoutMs: DETAIL_FETCH_TIMEOUT_MS,
      council: requestedCouncil,
      fetchImpl: detailFetch,
    });
  } catch (error) {
    console.warn("[planning-application] Failed to fetch application detail", reference, error);
    if (res) {
      // Keep crawlers and users on a valid, noindex recovery page when the
      // origin is rate-limited or temporarily unavailable. The application
      // page remains a normal 200 document; it must not turn a transient data
      // outage into a site-wide 5xx or an SEO error signal.
      res.statusCode = 200;
      res.setHeader("Cache-Control", "no-store");
    }
    return {
      props: {
        reference,
        error: "temporary_unavailable",
        councilSlug: requestedCouncil,
      },
    };
  }

  if (!application) {
    if (res) {
      res.statusCode = 404;
      setDetailPageCacheHeader(res, {
        sMaxage: DETAIL_PAGE_NOT_FOUND_S_MAXAGE_SECONDS,
        staleWhileRevalidate: DETAIL_PAGE_S_MAXAGE_SECONDS,
      });
    }
    return {
      props: {
        reference,
        councilSlug: requestedCouncil,
      },
    };
  }

  const normalizedApplication = normalizeApplicationRecord(application);
  const resolvedCouncil = getCouncilConfig(normalizedApplication?.source_council)?.slug || requestedCouncil;
  const sourceRecordOnly = isSourceOnlyPlanningRecord(normalizedApplication);
  const relatedApplicationsPromise = sourceRecordOnly || isIsr || isAutomatedDetailRequest
    ? Promise.resolve([])
    : fetchApplicationsList(resolvedCouncil)
        .then((applications) => buildRelatedApplicationsForStaticContent(application, applications))
        .catch((error) => {
          console.warn("[planning-application] Failed to fetch related applications", reference, error);
          return [];
        });

  // Newer API responses tell us whether a published article exists. Avoid a
  // second unique backend request for the common no-article case, while
  // preserving the previous lookup behavior during rolling deployments where
  // the marker may be absent.
  const publishedArticlePromise = sourceRecordOnly || normalizedApplication?.has_published_article === false
    ? Promise.resolve({ article: null, lookupComplete: true })
    : fetchPlanningArticleByReference(reference, {
        timeoutMs: PLANNING_ARTICLE_FETCH_TIMEOUT_MS,
        council: resolvedCouncil,
      })
        .then((article) => ({ article, lookupComplete: true }))
        .catch((error) => {
          console.warn("[planning-application] Failed to fetch planning article", reference, error);
          return { article: null, lookupComplete: false };
        });

  const [relatedApplications, publishedArticleResult] = await Promise.all([
    relatedApplicationsPromise,
    publishedArticlePromise,
  ]);

  setDetailPageCacheHeader(res);

  return {
    props: {
      application: normalizedApplication,
      relatedApplications,
      publishedArticle: publishedArticleResult.article,
      publishedArticleLookupComplete: publishedArticleResult.lookupComplete,
      councilSlug: resolvedCouncil,
    },
  };
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" };
}

export async function getStaticProps({ params }) {
  const result = await loadPlanningApplicationPageProps({
    params,
    query: {},
    isIsr: true,
  });

  // Vercel can expose and cache a thrown ISR regeneration as a 500 when an
  // origin outage overlaps a deployment or a cold regional cache. Always
  // return the existing noindex recovery experience so every cache state has a
  // bounded, successful response and retries after the short recovery window.
  if (result?.props?.error === "temporary_unavailable") {
    return {
      props: result.props,
      revalidate: DETAIL_PAGE_RECOVERY_REVALIDATE_SECONDS,
    };
  }

  if (!result?.props?.application) {
    return {
      notFound: true,
      revalidate: DETAIL_PAGE_NOT_FOUND_REVALIDATE_SECONDS,
    };
  }

  return {
    props: result.props,
    revalidate: getDetailPageRevalidateSeconds(result.props.application),
  };
}
